/**
 * Allow .eml files as chat attachments (issue #9).
 *
 * pi-web-ui's MessageEditor rejects .eml twice: the paperclip's `accept`
 * list doesn't include it, and attachment-utils throws
 * "Unsupported file type: message/rfc822" because it only knows text/*,
 * images, and office formats. An .eml *is* plain RFC-822 text, so the fix
 * is to present it to the pipeline as text/plain (keeping its filename):
 * the text extractor then inlines headers + body for the model.
 *
 * We patch at the MessageEditor boundary instead of forking the library:
 *  - createRenderRoot: extend acceptedTypes + wrap the instance's drop
 *    handler (a constructor-assigned arrow, so per-instance). NOTE: this
 *    hook is deliberate — customElements.define() *captures* lifecycle
 *    callbacks (connectedCallback & co) at definition time, so patching
 *    those on the prototype afterwards does nothing. createRenderRoot is
 *    invoked dynamically by Lit itself, once per element, before the
 *    first render — exactly what we need.
 *  - handleFilesSelected (prototype method): re-type .eml files on the
 *    input element before the original handler reads them.
 */

import { MessageEditor } from "@earendil-works/pi-web-ui";

function isEml(file: File): boolean {
	return file.name.toLowerCase().endsWith(".eml") && !file.type.startsWith("text/");
}

/** Same bytes, same name, text/plain type — attachment-utils' text branch
 *  accepts anything with a text/* mime. */
function asTextFile(file: File): File {
	return new File([file], file.name, { type: "text/plain", lastModified: file.lastModified });
}

function convertList(files: File[]): FileList {
	const dt = new DataTransfer();
	for (const f of files) dt.items.add(isEml(f) ? asTextFile(f) : f);
	return dt.files;
}

type EditorInternals = {
	acceptedTypes: string;
	handleDrop: (e: DragEvent) => Promise<void> | void;
};

const proto = MessageEditor.prototype as unknown as {
	createRenderRoot(): HTMLElement | DocumentFragment;
	handleFilesSelected(e: Event): Promise<void>;
};

const origCreateRenderRoot = proto.createRenderRoot;
proto.createRenderRoot = function (this: HTMLElement & EditorInternals) {
	if (!this.acceptedTypes.includes(".eml")) {
		this.acceptedTypes += ",.eml,message/rfc822";
	}
	const origDrop = this.handleDrop;
	this.handleDrop = (e: DragEvent) => {
		const files = Array.from(e.dataTransfer?.files ?? []);
		if (files.some(isEml)) {
			// Rebuild the event around a converted DataTransfer; the original
			// handler only touches preventDefault/stopPropagation/dataTransfer.
			e.preventDefault();
			e.stopPropagation();
			const synthetic = {
				preventDefault() {},
				stopPropagation() {},
				dataTransfer: (() => {
					const dt = new DataTransfer();
					for (const f of files) dt.items.add(isEml(f) ? asTextFile(f) : f);
					return dt;
				})(),
			} as unknown as DragEvent;
			return origDrop.call(this, synthetic);
		}
		return origDrop.call(this, e);
	};
	return origCreateRenderRoot.call(this);
};

const origSelected = proto.handleFilesSelected;
proto.handleFilesSelected = async function (e: Event) {
	const input = e.target as HTMLInputElement;
	const files = Array.from(input.files ?? []);
	if (files.some(isEml)) {
		// input.files is assignable from a DataTransfer's FileList.
		input.files = convertList(files);
	}
	return origSelected.call(this, e);
};
